import { SelectEntriesPayloadTypes } from '@/lib/enum/timesheets/timesheets';
import { SelectedEntriesType } from '@/lib/timesheets/timesheetBulkActions';
import { TimesheetEntry } from '../../api/v1';

export const defaultSelectedEntries = (): SelectedEntriesType => ({
  shifts: [],
  timesheets: [],
});

interface StoreState {
  selectedEntries: SelectedEntriesType;
}
type SelectedEntriesTypeKeysType = keyof SelectedEntriesType;

interface BaseSetEntriesPayload {
  select: boolean;
}

interface ShiftSetEntriesPayload extends BaseSetEntriesPayload {
  type: Extract<SelectedEntriesTypeKeysType, 'shifts'>;
  values: SelectedEntriesType['shifts'];
}
interface TimesheetSetEntriesPayload extends BaseSetEntriesPayload {
  type: Extract<SelectedEntriesTypeKeysType, 'timesheets'>;
  values: SelectedEntriesType['timesheets'];
}
type SetEntriesPayload = ShiftSetEntriesPayload | TimesheetSetEntriesPayload;

export default {
  namespaced: true,
  state: {
    selectedEntries: defaultSelectedEntries(),
  },
  getters: {
    getSelectedTimesheets(
      state: StoreState,
    ): SelectedEntriesType['timesheets'] {
      return state.selectedEntries.timesheets;
    },
  },
  mutations: {
    SET_SELECTED_ENTRIES(
      state: StoreState,
      selectedEntries: SelectedEntriesType,
    ) {
      state.selectedEntries = selectedEntries;
    },
    RESET_SELECTED_ENTRIES(state: StoreState) {
      state.selectedEntries = defaultSelectedEntries();
    },
  },
  actions: {
    unselectShiftsWithTimesheets(
      { commit, state }: { commit: any; state: StoreState },
      timesheetEntries: TimesheetEntry[],
    ) {
      // Unselect shifts which have timesheet entries created by other means
      // e.g. other users or non-bulk actions
      const clonedSelectedEntries = structuredClone(state.selectedEntries);
      clonedSelectedEntries.shifts = clonedSelectedEntries.shifts.filter(
        (s) => !timesheetEntries.some((t) => t.shiftId === s.id),
      );
      commit('SET_SELECTED_ENTRIES', clonedSelectedEntries);
    },
    selectEntries(
      { commit, state }: { commit: any; state: StoreState },
      payload: SetEntriesPayload,
    ): void {
      const clonedSelectedEntries = structuredClone(state.selectedEntries);
      if (payload.select) {
        // Filter as we don't want duplicate selected items
        if (payload.type === SelectEntriesPayloadTypes.Shifts) {
          clonedSelectedEntries[payload.type].push(
            ...payload.values.filter(
              (shift) =>
                !clonedSelectedEntries.shifts.some((s) => s.id === shift.id),
            ),
          );
        } else {
          clonedSelectedEntries[payload.type].push(
            ...payload.values.filter(
              (payloadEntry) =>
                !clonedSelectedEntries.timesheets.some(
                  (timesheetEntry) => timesheetEntry.id === payloadEntry.id,
                ),
            ),
          );
        }
        commit('SET_SELECTED_ENTRIES', clonedSelectedEntries);
        return;
      }

      if (payload.type === SelectEntriesPayloadTypes.Shifts) {
        clonedSelectedEntries[payload.type] =
          clonedSelectedEntries.shifts.filter(
            (shift) => !payload.values.some((s) => s.id === shift.id),
          );
      } else {
        clonedSelectedEntries[payload.type] =
          clonedSelectedEntries.timesheets.filter(
            (timesheetEntry) =>
              !payload.values.some(
                (payloadEntry) => payloadEntry.id === timesheetEntry.id,
              ),
          );
      }
      commit('SET_SELECTED_ENTRIES', clonedSelectedEntries);
    },
    resetSelectedEntries({ commit }) {
      commit('RESET_SELECTED_ENTRIES');
    },
    syncTimesheets(
      { commit, state }: { commit: any; state: StoreState },
      updatedEntries: TimesheetEntry[],
    ) {
      const clonedSelectedEntries = structuredClone(state.selectedEntries);
      if (clonedSelectedEntries.timesheets.length) {
        clonedSelectedEntries.timesheets = updatedEntries.filter((entry) =>
          clonedSelectedEntries.timesheets.some(
            (storedEntry) => entry.id === storedEntry.id,
          ),
        );
        commit('SET_SELECTED_ENTRIES', clonedSelectedEntries);
      }
    },
  },
};
